import { SystemProps } from '@chakra-ui/react';
import { useRef, useLayoutEffect, RefObject } from 'react';

type HiddenWrapEffect = (_children: HTMLElement[], _container: HTMLElement) => void;
export function useHiddenWrap<T extends HTMLElement>({
   wrappedChildrenEffect,
   shownChildrenEffect,
}: {
   wrappedChildrenEffect?: HiddenWrapEffect;
   shownChildrenEffect?: HiddenWrapEffect;
}): {
   ref: RefObject<T | null>;
   display: SystemProps['display'];
   flexWrap: SystemProps['flexWrap'];
   overflow: SystemProps['overflow'];
} {
   const ref = useRef<T | null>(null);
   const rafIdRef = useRef<number | null>(null);
   const wrappedChildrenEffectRef = useRef(wrappedChildrenEffect);
   const shownChildrenEffectRef = useRef(shownChildrenEffect);

   // Keep refs in sync with latest callbacks
   useLayoutEffect(() => {
      wrappedChildrenEffectRef.current = wrappedChildrenEffect;
      shownChildrenEffectRef.current = shownChildrenEffect;
   }, [wrappedChildrenEffect, shownChildrenEffect]);

   useLayoutEffect(() => {
      if (!ref?.current) {
         return;
      }

      const scheduleMeasure = () => {
         if (rafIdRef.current !== null) {
            cancelAnimationFrame(rafIdRef.current);
         }
         rafIdRef.current = requestAnimationFrame(() => {
            rafIdRef.current = null;
            if (!ref.current) {
               return;
            }
            onBrowserRender(
               ref.current,
               wrappedChildrenEffectRef.current,
               shownChildrenEffectRef.current
            );
         });
      };

      scheduleMeasure();
      window.addEventListener('resize', scheduleMeasure);
      return () => {
         window.removeEventListener('resize', scheduleMeasure);
         if (rafIdRef.current !== null) {
            cancelAnimationFrame(rafIdRef.current);
            rafIdRef.current = null;
         }
      };
   }, []);
   return { ref, display: 'flex', flexWrap: 'wrap', overflow: 'hidden' };
}

function onBrowserRender<T extends HTMLElement>(
   container: T,
   wrappedEffect?: HiddenWrapEffect,
   shownEffect?: HiddenWrapEffect
) {
   const buffer = container.offsetHeight / 2;
   const wrappedPX = container.offsetTop + buffer;
   const children = Array.from(container.children) as HTMLElement[];

   const wrappedChildren = children.filter(child => child.offsetTop > wrappedPX);
   const shownChildren = children.filter(child => child.offsetTop <= wrappedPX);
   wrappedEffect?.(wrappedChildren, container);
   shownEffect?.(shownChildren, container);
}
